/**
 * Ride UAT Validator
 * 
 * Validation service for ride upload operations.
 * Validates rides before upload to ensure data integrity and API compatibility.
 * 
 * @module rideUatValidator
 */

import type { RideSchema } from '../types/simulation';
import type { UserSearchResult, LocationSearchResult } from './rideUatClient';
import { validateRideExport } from './rideDataExporter';

/**
 * Validation result
 */
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Ride validation data for upload
 */
export interface RideValidationData {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  rideValidations: Map<string, {
    isValid: boolean;
    errors: string[];
    warnings: string[];
    userFound?: boolean;
    pickupLocationFound?: boolean;
    dropoffLocationFound?: boolean;
  }>;
}

/**
 * Validate prerequisites for ride operations
 * 
 * @param prerequisites - Prerequisites to validate
 * @returns Validation result
 */
export function validatePrerequisites(prerequisites: {
  jwtToken?: string;
  productId?: string;
  networkId?: number | null;
  downloadAcknowledged?: boolean;
}): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!prerequisites.jwtToken || prerequisites.jwtToken.trim() === '') {
    errors.push('JWT token is required');
  }

  if (!prerequisites.productId || prerequisites.productId.trim() === '') {
    errors.push('Product ID is required');
  }

  if (prerequisites.networkId === null || prerequisites.networkId === undefined) {
    errors.push('Network ID is required');
  }

  if (!prerequisites.downloadAcknowledged) {
    errors.push('Download acknowledgment is required');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate user exists
 * 
 * @param userId - User ID to validate
 * @param userSearchResults - Results from user search
 * @returns Validation result
 */
export function validateUser(
  userId: string,
  userSearchResults: UserSearchResult[]
): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!userId || userId.trim() === '') {
    errors.push('User ID is required');
    return { isValid: false, errors, warnings };
  }

  const userFound = userSearchResults.some(u => u.id === userId);
  if (!userFound) {
    errors.push(`User ${userId} not found in search results`);
  }

  if (userSearchResults.length > 1) {
    warnings.push(`Multiple users found (${userSearchResults.length}), using first match`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate locations can be found
 * 
 * @param pickupLocation - Pickup location search result
 * @param dropoffLocation - Dropoff location search result
 * @returns Validation result
 */
export function validateLocations(
  pickupLocation: LocationSearchResult | null,
  dropoffLocation: LocationSearchResult | null
): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!pickupLocation) {
    errors.push('Pickup location not found');
  } else {
    if (!pickupLocation.placeId) {
      warnings.push('Pickup location missing place ID');
    }
  }

  if (!dropoffLocation) {
    errors.push('Dropoff location not found');
  } else {
    if (!dropoffLocation.placeId) {
      warnings.push('Dropoff location missing place ID');
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate time format and constraints
 * 
 * @param timeString - Time string to validate
 * @param tripType - Trip type (leaveAt or arriveBy)
 * @returns Validation result
 */
export function validateTime(
  timeString: string | undefined,
  tripType: 'leaveAt' | 'arriveBy'
): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!timeString || timeString.trim() === '') {
    errors.push('Request time is required');
    return { isValid: false, errors, warnings };
  }

  try {
    const timeDate = new Date(timeString);
    if (isNaN(timeDate.getTime())) {
      errors.push('Invalid time format');
      return { isValid: false, errors, warnings };
    }

    // For leaveAt, time should not be in the past
    if (tripType === 'leaveAt') {
      const now = new Date();
      if (timeDate < now) {
        warnings.push('Request time is in the past (may cause issues)');
      }
    }
  } catch {
    errors.push('Invalid time format');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate passenger data
 * 
 * @param passengers - Passenger array to validate
 * @returns Validation result
 */
export function validatePassengers(
  passengers: Array<{ type: string; number_of_passengers: number }>
): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!passengers || passengers.length === 0) {
    errors.push('At least one passenger is required');
    return { isValid: false, errors, warnings };
  }

  passengers.forEach((passenger, index) => {
    if (!passenger.type) {
      errors.push(`Passenger ${index + 1} is missing type`);
    } else if (passenger.type !== 'standard' && passenger.type !== 'wheelchair') {
      warnings.push(`Passenger ${index + 1} has unusual type: ${passenger.type}`);
    }

    if (passenger.number_of_passengers === undefined || passenger.number_of_passengers < 1) {
      errors.push(`Passenger ${index + 1} must have at least 1 passenger`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate ride for upload
 * 
 * @param ride - RideSchema to validate
 * @param userSearchResults - Results from user search (optional)
 * @param pickupLocation - Pickup location search result (optional)
 * @param dropoffLocation - Dropoff location search result (optional)
 * @returns Validation result with per-ride details
 */
export function validateRideForUpload(
  ride: RideSchema,
  userSearchResults?: UserSearchResult[],
  pickupLocation?: LocationSearchResult | null,
  dropoffLocation?: LocationSearchResult | null
): {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  userFound?: boolean;
  pickupLocationFound?: boolean;
  dropoffLocationFound?: boolean;
} {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Basic ride validation
  const exportValidation = validateRideExport(ride);
  errors.push(...exportValidation.errors);
  warnings.push(...exportValidation.warnings);

  // Validate user
  const userId = ride.user_id || ride.external_user_reference_id;
  let userFound = false;
  if (userId && userSearchResults) {
    userFound = userSearchResults.some(u => u.id === userId);
    if (!userFound) {
      errors.push(`User ${userId} not found`);
    }
  } else if (!userId) {
    errors.push('User ID is required');
  }

  // Validate locations
  const pickupLocationFound = !!pickupLocation;
  const dropoffLocationFound = !!dropoffLocation;
  
  if (!pickupLocation) {
    errors.push('Pickup location not found');
  }
  if (!dropoffLocation) {
    errors.push('Dropoff location not found');
  }

  // Validate time
  const tripType: 'leaveAt' | 'arriveBy' = 
    ride.request_type === 'arriveBy' ? 'arriveBy' : 'leaveAt';
  const timeValidation = validateTime(ride.request_time, tripType);
  errors.push(...timeValidation.errors);
  warnings.push(...timeValidation.warnings);

  // Validate passengers
  const passengerValidation = validatePassengers(ride.passengers || []);
  errors.push(...passengerValidation.errors);
  warnings.push(...passengerValidation.warnings);

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    userFound,
    pickupLocationFound,
    dropoffLocationFound,
  };
}

/**
 * Validate multiple rides for upload
 * 
 * @param rides - Array of RideSchema to validate
 * @param userSearchResultsMap - Map of ride ID to user search results (optional)
 * @param locationSearchResultsMap - Map of ride ID to location search results (optional)
 * @returns Aggregated validation result
 */
export function validateRidesForUpload(
  rides: RideSchema[],
  userSearchResultsMap?: Map<string, UserSearchResult[]>,
  locationSearchResultsMap?: Map<string, { pickup: LocationSearchResult | null; dropoff: LocationSearchResult | null }>
): RideValidationData {
  const errors: string[] = [];
  const warnings: string[] = [];
  const rideValidations = new Map<string, {
    isValid: boolean;
    errors: string[];
    warnings: string[];
    userFound?: boolean;
    pickupLocationFound?: boolean;
    dropoffLocationFound?: boolean;
  }>();

  rides.forEach((ride) => {
    const rideId = ride.ride_id || ride.external_reference_id || 'unknown';
    const userSearchResults = userSearchResultsMap?.get(rideId);
    const locationResults = locationSearchResultsMap?.get(rideId);
    
    const validation = validateRideForUpload(
      ride,
      userSearchResults,
      locationResults?.pickup,
      locationResults?.dropoff
    );

    rideValidations.set(rideId, validation);

    if (!validation.isValid) {
      errors.push(`Ride ${rideId}: ${validation.errors.join(', ')}`);
    }
    if (validation.warnings.length > 0) {
      warnings.push(`Ride ${rideId}: ${validation.warnings.join(', ')}`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    rideValidations,
  };
}

